/**
 * Gemini Live model registry.
 *
 * Single source of truth for every selectable Gemini Live model used by the
 * voice relay. Mirrors the OpenAI realtime registry shape (model identifier,
 * per-modality pricing) so the same `TokenUsageCostAdapter` math works for
 * both providers without branching by provider.
 *
 * Pricing values are USD per 1M tokens. Thinking tokens are output — Google's
 * pricing table reads "Output price (including thinking tokens)" — and a Live
 * session reports them in `thoughtsTokenCount`, outside `totalTokenCount`. The
 * Gemini adapter adds them to TEXT output (`attributeGeminiUsage`), so they are
 * priced at `textOutput` here and need no field of their own.
 *
 * Cached input is not offered for any Live model — each model page lists
 * "Caching: Not supported" — so both cached rates are 0, and Gemini sessions
 * report no cached tokens: `GeminiUsageAccumulator` emits
 * `inputCachedTokens = 0`, and no recorded `usageMetadata` frame carries a
 * cached count.
 *
 * gemini-3.8-live and gemini-3.1-flash-live-preview share one row of Google's
 * pricing table, so their rates are identical.
 *
 * @see https://ai.google.dev/gemini-api/docs/pricing
 */

import { z } from "zod";

import { type GeminiLiveConnectionEnd } from "./GeminiLiveConnectionEnd.schema";
import { LIVE_API_SETTING_TUNABLES } from "./GeminiLiveSettingTunables.schema";
import {
  GEMINI_LIVE_FUNCTION_BEHAVIORS,
  GEMINI_LIVE_FUNCTION_RESPONSE_SCHEDULINGS,
  GEMINI_LIVE_THINKING_LEVELS,
  type GeminiLiveTunables
} from "./GeminiLiveTunables.schema";
import {
  DEFAULT_GEMINI_LIVE_TURN_DETECTION,
  type GeminiLiveTurnDetection
} from "./GeminiLiveTurnDetection.schema";
import { type RealtimeTokenPricing } from "./RealtimeTokenPricing.schema";

// ============================================================
// Model registry
// ============================================================

export const GEMINI_LIVE_MODEL_IDS = [
  "models/gemini-3.1-flash-live-preview",
  "models/gemini-3.8-live"
] as const;
export type GeminiLiveModelId = (typeof GEMINI_LIVE_MODEL_IDS)[number];
export const GeminiLiveModelIdSchema = z.enum(GEMINI_LIVE_MODEL_IDS);

/**
 * Per-model runtime capabilities — the one table the setup builder and the
 * settings UI both read to decide what a model is told and what is shown for
 * it.
 */
export interface GeminiLiveCapabilities {
  /**
   * Whether the model honors `setup.generationConfig.speechConfig.languageCode`.
   *
   * Native-audio output models (the current Gemini Live preview family) infer
   * language from the incoming audio + system instructions and explicitly
   * "don't support setting the language code"
   * (https://ai.google.dev/gemini-api/docs/live-api/capabilities). Sending
   * one is at best ignored; at worst rejected — and a rejected `setup`
   * payload silently drops the entire session config, leaving the model on
   * its defaults (no voice, no instructions, no tools). The same silent-drop
   * class of bug as the OpenAI Realtime GA `session.update`.
   *
   * Half-cascade / cascade variants do honor the field. Flip to `true` per
   * model when introducing one of those.
   */
  honorsLanguageCode: boolean;
  /**
   * Per-tunable rule: configurable, fixed, or unsupported, with the page that
   * states it and whether a recorded session has confirmed it. See
   * `GeminiLiveTunables.schema.ts` for what each state obliges a consumer to do.
   */
  tunables: GeminiLiveTunables;
  /** How the server ends a connection on this model — see {@link GeminiLiveConnectionEnd}. */
  connectionEnd: GeminiLiveConnectionEnd;
  /**
   * The turn detection a deployment on this model runs with when it stores none.
   *
   * A stored `geminiTurnDetection` is a choice and always wins. Creation stores
   * none, so a new deployment follows its model — including across a model
   * switch — until someone sets it. Read it through
   * {@link resolveGeminiLiveTurnDetection}, never off the row directly.
   */
  defaultTurnDetection: GeminiLiveTurnDetection;
}

/**
 * Google's lifecycle listing for a model it has marked for replacement, as its
 * deprecations page states it. The settings UI shows it on the model's card;
 * nothing moves a deployment off the model because of it — that is a decision
 * about customers, taken by rewriting their stored settings.
 */
export interface GeminiLiveModelDeprecation {
  /** The page's "Recommended replacement". */
  replacement: GeminiLiveModelId;
  /** The page's "Shutdown date" as an ISO date, or `null` while it reads "No shutdown date announced". */
  shutdownDate: string | null;
  doc: string;
}

export interface GeminiLiveModel {
  id: GeminiLiveModelId;
  displayName: string;
  contextWindowTokens: number;
  /**
   * Length at which an oversized `systemInstruction` starts producing a signal.
   * Content is NOT changed at this threshold — see `maxInstructionsChars`.
   *
   * Google publishes no character cap for `systemInstruction`; the real
   * constraint is the context window, which the whole conversation shares. So
   * unlike OpenAI — where the catalog value is a hard wire threshold — this is
   * a product judgement about how much of the window one agent's configuration
   * should be allowed to occupy before someone is told about it.
   *
   * Set to match `OPENAI_REALTIME_MODELS["gpt-realtime-2"].maxInstructionsChars`
   * so the two providers report the same agents as oversized. A production
   * agent measured at 118,110 rendered chars sits well above it, which is
   * exactly the case that used to pass through this adapter unremarked.
   */
  softInstructionsChars: number;
  /**
   * Length above which `systemInstruction` is truncated before it is sent.
   *
   * Deliberately far above `softInstructionsChars`: this provider has never
   * truncated, so adopting the soft value here would silently shorten prompts
   * that work today. Observability first, behaviour later — lower it with
   * measurements from the soft-limit signal, not with an assumption.
   */
  maxInstructionsChars: number;
  pricing: RealtimeTokenPricing;
  capabilities: GeminiLiveCapabilities;
  /** Set when Google lists the model for replacement; `null` while it does not. */
  deprecation: GeminiLiveModelDeprecation | null;
}

/**
 * Narrows a free-form string to a model id this table actually carries.
 *
 * See `isOpenAIRealtimeModelId` — `in` walks the prototype chain and this does
 * not.
 */
export function isGeminiLiveModelId(value: string): value is GeminiLiveModelId {
  return (GEMINI_LIVE_MODEL_IDS as readonly string[]).includes(value);
}

const GEMINI_31_MODEL_PAGE =
  "https://ai.google.dev/gemini-api/docs/models/gemini-3.1-flash-live-preview";
const GEMINI_38_MODEL_PAGE = "https://ai.google.dev/gemini-api/docs/models/gemini-3.8-live";
const GEMINI_API_REFERENCE = "https://ai.google.dev/api/generate-content";
const GEMINI_DEPRECATIONS_PAGE = "https://ai.google.dev/gemini-api/docs/deprecations";

export const GEMINI_LIVE_MODELS: Record<GeminiLiveModelId, GeminiLiveModel> = {
  "models/gemini-3.1-flash-live-preview": {
    id: "models/gemini-3.1-flash-live-preview",
    displayName: "Gemini 3.1 Flash Live",
    contextWindowTokens: 128_000,
    softInstructionsChars: 60_000,
    // ~50k tokens of a 128k window, leaving the clear majority for the
    // conversation. Above every prompt observed in production.
    maxInstructionsChars: 200_000,
    pricing: {
      audioInput: 3.0,
      audioInputCached: 0,
      audioOutput: 12.0,
      textInput: 0.75,
      textInputCached: 0,
      textOutput: 4.5
    },
    // Live API models → Preview models: "gemini-3.1-flash-live-preview | March
    // 11, 2026 | No shutdown date announced | gemini-3.8-live".
    deprecation: {
      replacement: "models/gemini-3.8-live",
      shutdownDate: null,
      doc: GEMINI_DEPRECATIONS_PAGE
    },
    capabilities: {
      // Native-audio model — see `honorsLanguageCode` docblock for the
      // silent-drop risk this gate prevents.
      honorsLanguageCode: false,
      // Google's documented default. Measured against the relay's own setup frame
      // with a caller pausing 900 / 1600 / 2400 ms mid-order-number, 3.1 ends with
      // the whole number on HIGH and on LOW alike (the 3.8 loss below does not
      // occur here), so it keeps HIGH.
      defaultTurnDetection: DEFAULT_GEMINI_LIVE_TURN_DETECTION,
      // A row is `confirmed` where a recording under
      // apps/backend/src/voice-relay/infrastructure/adapters/gemini/__fixtures__/
      // shows the rule on the socket, named beside it; `provisional` where only
      // Google's pages have been read.
      tunables: {
        thinkingLevel: {
          support: "configurable",
          values: GEMINI_LIVE_THINKING_LEVELS,
          // "The default is minimal to optimize for lowest latency."
          providerDefault: "minimal",
          doc: GEMINI_31_MODEL_PAGE,
          // Accepted nested in generationConfig, either case:
          // 02-31-thinking-nested-upper, -lower, 14-31-production-setup.
          wire: "confirmed"
        },
        // "Proactive audio and affective dialogue: These features are not yet
        // supported in Gemini 3.1 Flash Live. Remove any configuration."
        // `proactivity` is refused as an unknown field: 03-31-proactivity-true.
        proactiveAudio: { support: "unsupported", doc: GEMINI_31_MODEL_PAGE, wire: "confirmed" },
        affectiveDialog: { support: "unsupported", doc: GEMINI_31_MODEL_PAGE, wire: "provisional" },
        // "Async function calling: Not yet supported. Function calling is
        // synchronous only." The field itself is ACCEPTED, either value — the
        // setup completes and the model greets (17-31-tool-behavior-blocking,
        // -nonblocking) — but what a NON_BLOCKING call then does is unrecorded,
        // so nothing is sent and the row stays provisional.
        functionBehavior: {
          support: "fixed",
          value: "BLOCKING",
          doc: GEMINI_31_MODEL_PAGE,
          wire: "provisional"
        },
        // `scheduling` applies to NON_BLOCKING calls only and "is ignored
        // otherwise" — and this model has no NON_BLOCKING calls. Sent anyway it
        // is accepted and changes nothing: the result is spoken as a plain one
        // (17-31-tool-scheduling).
        functionResponseScheduling: {
          support: "unsupported",
          doc: GEMINI_API_REFERENCE,
          wire: "confirmed"
        },
        // `willContinue` "Is only applicable to NON_BLOCKING function calls, is
        // ignored otherwise." Sent anyway, the interim is taken as THE result:
        // spoken as the answer, the real result never spoken, and in one run a
        // status the tool never returned was invented while it waited
        // (11-31-bridge-interim-interrupt, -when-idle, -invented).
        functionResponseWillContinue: {
          support: "unsupported",
          doc: GEMINI_API_REFERENCE,
          wire: "confirmed"
        },
        ...LIVE_API_SETTING_TUNABLES
      },
      // GoAway at 540.6 s and again at 548.0 s, both `timeLeft: "50s"`, then
      // close 1008 at 598.4 s on a client that stayed; 627 updates in 598 s.
      connectionEnd: {
        goAway: "sent",
        firstGoAwayAfterSeconds: 540,
        goAwayTimeLeftSeconds: 50,
        resumptionUpdates: "about-every-second",
        evidence:
          "apps/backend/src/voice-relay/infrastructure/adapters/gemini/__fixtures__/08-31-goaway.jsonl"
      }
    }
  },
  "models/gemini-3.8-live": {
    id: "models/gemini-3.8-live",
    displayName: "Gemini 3.8 Live",
    // The model page lists a 131,072-token input limit; 128_000 matches the
    // capabilities guide's "128k tokens for native audio output models" and
    // the 3.1 entry, so both models budget the window identically.
    contextWindowTokens: 128_000,
    softInstructionsChars: 60_000,
    maxInstructionsChars: 200_000,
    pricing: {
      audioInput: 3.0,
      audioInputCached: 0,
      audioOutput: 12.0,
      textInput: 0.75,
      textInputCached: 0,
      textOutput: 4.5
    },
    deprecation: null,
    capabilities: {
      // Native-audio model: "Native audio output models automatically choose
      // the appropriate language and don't support explicitly setting the
      // language code." (capabilities guide)
      honorsLanguageCode: false,
      // END_SENSITIVITY_LOW, because HIGH loses the start of an utterance the
      // caller pauses in. Measured against 3.8 with the relay's own setup frame,
      // a caller saying "…the number is four five", pausing, then "seven one
      // two": on HIGH the transcript stops at "…my order? The", the model asks
      // for the number, and the tool is called with "712" — 11/11 across pauses
      // of 900, 1600 and 2400 ms. On LOW the last call carries "45712" 11/11.
      // It costs ~0.45 s on an unpaused question (question end to tool call
      // 893–1331 ms HIGH, 1344–1654 ms LOW). A longer `silenceDurationMs` on
      // HIGH does not help: 2000 ms still called with "712" 5/5 and cost ~1 s.
      defaultTurnDetection: {
        startSensitivity: "high",
        endSensitivity: "low",
        interruptResponse: true
      },
      // Evidence per row — see the 3.1 entry.
      tunables: {
        // "thinking_level is not supported for gemini-3.8-live. Omit
        // thinking_level (or thinking_config) from your session setup."
        // Refused with 1007 "Thinking level is not supported for this model.":
        // 03-38-thinking-nested.
        thinkingLevel: { support: "unsupported", doc: GEMINI_38_MODEL_PAGE, wire: "confirmed" },
        // "Proactive audio is now permanently enabled. Setting
        // proactive_audio: false returns an error." v1alpha refuses `false`
        // (03-38-proactivity-false-v1alpha); v1beta has no `proactivity` field
        // at all (03-38-proactivity-true), so the value is only ever omitted.
        proactiveAudio: {
          support: "fixed",
          value: true,
          doc: GEMINI_38_MODEL_PAGE,
          wire: "confirmed"
        },
        // "Affective dialogue is removed from the API." The capabilities guide
        // still documents `enable_affective_dialog` as a v1beta feature, noting
        // only that 3.1 lacks it — the model page is the later and narrower
        // statement, and omitting the field is safe under either reading.
        // Refused as an unknown field on both endpoints: 03-38-affective,
        // 03-38-affective-v1alpha.
        affectiveDialog: { support: "unsupported", doc: GEMINI_38_MODEL_PAGE, wire: "confirmed" },
        // "Async execution (behavior: NON_BLOCKING) is now the default function
        // calling mode … setting behavior: BLOCKING" keeps the old mode.
        // The API reference says `Behavior` "Defaults to BLOCKING" — the two
        // disagree about an OMITTED field, and the socket sides with the model
        // page: an omitted behaviour runs exactly like NON_BLOCKING, a `call_N`
        // id and the turn ending at once (06-38-tool-default-behavior against
        // 06-38-tool-nonblocking-*), where BLOCKING gets an `fc_` id and holds
        // the turn (06-38-tool-blocking). The builder sends it explicitly anyway.
        functionBehavior: {
          support: "configurable",
          values: GEMINI_LIVE_FUNCTION_BEHAVIORS,
          providerDefault: "NON_BLOCKING",
          doc: GEMINI_38_MODEL_PAGE,
          wire: "confirmed"
        },
        // "Function scheduling (SILENT, WHEN_IDLE, INTERRUPTED) is supported."
        // Values and default are the API reference's, which spells the third
        // `INTERRUPT` and states "Defaults to WHEN_IDLE". Top-level on the
        // response, INTERRUPT is spoken 660 ms and WHEN_IDLE 543 ms after the
        // result is sent, and SILENT is not spoken (06-38-tool-nonblocking-*).
        functionResponseScheduling: {
          support: "configurable",
          values: GEMINI_LIVE_FUNCTION_RESPONSE_SCHEDULINGS,
          providerDefault: "WHEN_IDLE",
          doc: GEMINI_API_REFERENCE,
          wire: "confirmed"
        },
        // "Signals that function call continues, and more responses will be
        // returned, turning the function call into a generator." An interim with
        // `willContinue: true` is spoken as progress, and the final result with
        // `false` is spoken after it (11-38-bridge-interim-*, 12-38-bargein-
        // interim-*, 13-38-fast-interim-*). Omitted, it is `false`.
        functionResponseWillContinue: {
          support: "configurable",
          values: [true, false],
          providerDefault: false,
          doc: GEMINI_API_REFERENCE,
          wire: "confirmed"
        },
        ...LIVE_API_SETTING_TUNABLES
      },
      // One update, 0.9 s in; no GoAway and no server close until the probe
      // closed the socket at 960.8 s. What happens past that is unmeasured.
      connectionEnd: {
        goAway: "not-observed",
        heldWithoutGoAwaySeconds: 960,
        resumptionUpdates: "once-per-connection",
        evidence:
          "apps/backend/src/voice-relay/infrastructure/adapters/gemini/__fixtures__/08-38-goaway.jsonl"
      }
    }
  }
};

/**
 * Model a NEW Gemini Live voice config is created with — by the editor's
 * defaults and, for a client that names none, by `stampGeminiLiveModelOnBirth`.
 */
export const DEFAULT_GEMINI_LIVE_MODEL: GeminiLiveModelId = "models/gemini-3.8-live";

/**
 * Model a deployment runs on when its settings name none — every row written
 * before `geminiModel` existed, all of which ran on this model.
 *
 * Deliberately NOT {@link DEFAULT_GEMINI_LIVE_MODEL}: changing what new
 * deployments get must not move existing customers to another model on their
 * next call. Moving them is a decision about customers, made by rewriting
 * their stored settings, never a side effect of editing a default.
 */
export const GEMINI_LIVE_MODEL_FOR_UNSET_DEPLOYMENTS: GeminiLiveModelId =
  "models/gemini-3.1-flash-live-preview";

/**
 * The turn detection a deployment's Gemini sessions run with: its stored choice,
 * or its model's {@link GeminiLiveCapabilities.defaultTurnDetection} when it
 * stored none. The one place an absent value is given one.
 */
export function resolveGeminiLiveTurnDetection(
  stored: GeminiLiveTurnDetection | undefined,
  modelId: GeminiLiveModelId
): GeminiLiveTurnDetection {
  return stored ?? GEMINI_LIVE_MODELS[modelId].capabilities.defaultTurnDetection;
}

/**
 * The stored per-deployment `geminiModel` preference.
 *
 * A plain string on READ, for the reasons `GptLiveDelegationModelSchema`
 * gives: the voice editor parses these settings and renders nothing when the
 * parse fails, and a deployment must keep parsing after a model is retired
 * from this registry. An id this registry does not carry is refused on WRITE
 * (`requireKnownGeminiLiveModel`) and resolved to
 * {@link GEMINI_LIVE_MODEL_FOR_UNSET_DEPLOYMENTS} on read
 * ({@link resolveGeminiLiveModelId}). Absent → the same.
 */
export const GeminiLiveModelSettingSchema = z.string().optional();

/**
 * The model a deployment's Gemini sessions run on, from its stored preference.
 * The one place an absent or unrecognised value is given a model.
 */
export function resolveGeminiLiveModelId(stored: string | undefined): GeminiLiveModelId {
  return stored !== undefined && isGeminiLiveModelId(stored)
    ? stored
    : GEMINI_LIVE_MODEL_FOR_UNSET_DEPLOYMENTS;
}
